import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home, CalendarDays, MessageSquare, User, Plus, X,
  Building2, Users, UserPlus, CalendarPlus, Trophy, Mail, Search,
} from "lucide-react";

const NAV = [
  { key: "home", label: "Home", to: "/live", icon: Home, end: true },
  { key: "schedule", label: "Schedule", to: "/live/schedule", icon: CalendarDays },
  { key: "messages", label: "Messages", to: "/live/messages", icon: MessageSquare },
  { key: "account", label: "Account", to: "/live/account", icon: User },
];

export default function LiveLayout({ children }) {
  const [sheet, setSheet] = useState(false);
  const [modal, setModal] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [manager, setManager] = useState(false);

  useEffect(() => {
    if (user) api.get("/live/me/teams").then(({ data }) => setManager(data.is_manager)).catch(() => {});
  }, [user]);

  const openModal = (m) => { setSheet(false); if (!user) { toast.error("Sign in to continue"); nav("/login"); return; } setModal(m); };

  const isActive = (item) => item.end ? pathname === "/live" || pathname === "/live/" : pathname.startsWith(item.to);

  return (
    <div className="min-h-screen bg-[#F1F3F6]">
      <Header />
      <main className="pb-24 min-h-[70vh]">{children}</main>

      {/* Bottom nav */}
      <nav data-testid="live-bottom-nav" className="fixed bottom-0 inset-x-0 z-40 bg-[#0A192F] text-white border-t border-white/10">
        <div className="max-w-lg mx-auto grid grid-cols-5 items-end px-2">
          {NAV.slice(0, 2).map((n) => <NavBtn key={n.key} n={n} active={isActive(n)} />)}
          <div className="flex justify-center">
            <button data-testid="live-plus-btn" onClick={() => setSheet(true)}
              className="-mt-6 w-14 h-14 rounded-full bg-[#E63946] grid place-items-center shadow-lg shadow-[#E63946]/30 hover:bg-[#c62f3b] transition-colors">
              <Plus className="w-7 h-7" />
            </button>
          </div>
          {NAV.slice(2).map((n) => <NavBtn key={n.key} n={n} active={isActive(n)} />)}
        </div>
      </nav>

      {/* Plus sheet */}
      {sheet && (
        <div className="fixed inset-0 z-50" data-testid="live-plus-sheet">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSheet(false)} />
          <div className="absolute bottom-0 inset-x-0 bg-white rounded-t-2xl p-5 pb-8 animate-in slide-in-from-bottom duration-200">
            <div className="flex items-center justify-between mb-4">
              <div className="label-overline">Create & Join</div>
              <button onClick={() => setSheet(false)} className="p-1 text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto">
              <SheetItem icon={Users} label="Create Team" testid="plus-create-team" onClick={() => openModal("team")} />
              <SheetItem icon={UserPlus} label="Join Team" testid="plus-join-team" onClick={() => openModal("join")} />
              <SheetItem icon={Building2} label="Create Org" testid="plus-create-org" onClick={() => openModal("org")} />
              {manager && <>
                <SheetItem icon={CalendarPlus} label="Schedule Game" testid="plus-schedule-game" onClick={() => openModal("schedule")} />
                <SheetItem icon={Trophy} label="Create Tournament" testid="plus-create-tournament" onClick={() => { setSheet(false); toast("Tournaments arrive in a later phase."); }} />
                <SheetItem icon={Mail} label="Invite Coach" testid="plus-invite-coach" onClick={() => { setSheet(false); toast("Coach invites arrive in a later phase."); }} />
              </>}
            </div>
          </div>
        </div>
      )}

      {modal === "org" && <CreateOrgModal onClose={() => setModal(null)} onDone={() => { setModal(null); nav("/live/account"); }} />}
      {modal === "team" && <CreateTeamModal onClose={() => setModal(null)} onDone={(slug) => { setModal(null); nav(`/live/teams/${slug}`); }} />}
      {modal === "join" && <JoinTeamModal onClose={() => setModal(null)} />}
      {modal === "schedule" && <ScheduleGameModal onClose={() => setModal(null)} onPick={(slug) => { setModal(null); nav(`/live/teams/${slug}`); }} />}
    </div>
  );
}

function NavBtn({ n, active }) {
  const Icon = n.icon;
  return (
    <Link to={n.to} data-testid={`live-nav-${n.key}`}
      className={`flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${active ? "text-[#E63946]" : "text-white/50 hover:text-white"}`}>
      <Icon className="w-5 h-5" /> {n.label}
    </Link>
  );
}
const SheetItem = ({ icon: Icon, label, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className="flex flex-col items-center gap-2 p-3 border border-slate-200 rounded-lg hover:border-[#E63946] hover:bg-slate-50 transition-colors">
    <div className="w-10 h-10 rounded-full bg-[#0A192F] grid place-items-center"><Icon className="w-5 h-5 text-[#E63946]" /></div>
    <span className="text-xs font-semibold text-[#0A192F] text-center leading-tight">{label}</span>
  </button>
);

/* ------------------------------------------------------------------ Modals */
const PROVINCES = ["", "ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE"];
const DIVISIONS = ["", "18U", "16U", "15U", "14U", "13U", "12U"];

function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid={testid}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-lg p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl uppercase tracking-tight text-[#0A192F]">{title}</h3>
          <button onClick={onClose} className="p-1 text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
const Field = (props) => <input {...props} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mb-3 focus:border-[#0A192F] outline-none" />;
const Sel = ({ opts, ...p }) => <select {...p} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mb-3">{opts.map((o) => <option key={o} value={o}>{o || "Select…"}</option>)}</select>;
const SubmitBtn = ({ children, ...p }) => <button {...p} className="w-full bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm hover:bg-[#c62f3b] transition-colors disabled:opacity-50">{children}</button>;

function CreateOrgModal({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", type: "club", province: "", city: "" });
  const [busy, setBusy] = useState(false);
  const submit = () => {
    if (!f.name.trim()) return toast.error("Organization name required");
    setBusy(true);
    api.post("/live/organizations", f).then(() => { toast.success("Organization created"); onDone(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setBusy(false));
  };
  return (
    <Modal title="Create Organization" onClose={onClose} testid="create-org-modal">
      <Field data-testid="org-name" placeholder="Organization name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <Sel data-testid="org-type" opts={["club", "academy", "association", "cbr_nation"]} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} />
      <div className="grid grid-cols-2 gap-3">
        <Sel data-testid="org-province" opts={PROVINCES} value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} />
        <Field data-testid="org-city" placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
      </div>
      <SubmitBtn data-testid="org-submit" disabled={busy} onClick={submit}>Create Organization</SubmitBtn>
    </Modal>
  );
}

function CreateTeamModal({ onClose, onDone }) {
  const [orgs, setOrgs] = useState(null);
  const [f, setF] = useState({ organization_id: "", name: "", age_division: "18U", province: "", league: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/live/me/teams").then(({ data }) => { setOrgs(data.organizations); if (data.organizations[0]) setF((s) => ({ ...s, organization_id: data.organizations[0].id })); }).catch(() => setOrgs([])); }, []);
  const submit = () => {
    if (!f.organization_id) return toast.error("Create an organization first");
    if (!f.name.trim()) return toast.error("Team name required");
    setBusy(true);
    const { organization_id, ...body } = f;
    api.post(`/live/organizations/${organization_id}/teams`, body)
      .then(({ data }) => { toast.success("Team created"); onDone(data.slug); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setBusy(false));
  };
  return (
    <Modal title="Create Team" onClose={onClose} testid="create-team-modal">
      {orgs === null ? <div className="text-sm text-slate-400 py-4">Loading…</div>
        : orgs.length === 0 ? <div className="text-sm text-slate-500 py-4">You need an organization first. Close this and choose <span className="font-semibold">Create Org</span>.</div>
        : <>
          <label className="label-overline">Organization</label>
          <select data-testid="team-org" value={f.organization_id} onChange={(e) => setF({ ...f, organization_id: e.target.value })} className="w-full border border-slate-200 rounded-sm px-3 py-2 text-sm mb-3 mt-1">
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <Field data-testid="team-name" placeholder="Team name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Sel data-testid="team-division" opts={DIVISIONS.filter(Boolean)} value={f.age_division} onChange={(e) => setF({ ...f, age_division: e.target.value })} />
            <Sel data-testid="team-province" opts={PROVINCES} value={f.province} onChange={(e) => setF({ ...f, province: e.target.value })} />
          </div>
          <Field data-testid="team-league" placeholder="League (optional)" value={f.league} onChange={(e) => setF({ ...f, league: e.target.value })} />
          <SubmitBtn data-testid="team-submit" disabled={busy} onClick={submit}>Create Team</SubmitBtn>
        </>}
    </Modal>
  );
}

function JoinTeamModal({ onClose }) {
  const [q, setQ] = useState("");
  const [teams, setTeams] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/live/teams").then(({ data }) => setTeams(data)).catch(() => {}); }, []);
  const filtered = teams.filter((t) => t.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Modal title="Join a Team" onClose={onClose} testid="join-team-modal">
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="join-search" placeholder="Search teams…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full border border-slate-200 rounded-sm pl-9 pr-3 py-2 text-sm outline-none focus:border-[#0A192F]" />
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {filtered.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No teams found.</div>
          : filtered.map((t) => (
            <button key={t.id} data-testid={`join-team-${t.id}`} onClick={() => { onClose(); nav(`/live/teams/${t.slug}`); }}
              className="w-full flex items-center gap-3 p-2 border border-slate-100 rounded-sm hover:border-[#E63946] text-left">
              <div className="w-8 h-8 bg-slate-100 rounded-sm grid place-items-center shrink-0 text-[10px] text-slate-400">{t.name[0]}</div>
              <div className="min-w-0"><div className="text-sm font-semibold truncate">{t.name}</div>
                <div className="text-[11px] text-slate-400">{[t.age_division, t.province].filter(Boolean).join(" · ")}</div></div>
            </button>
          ))}
      </div>
    </Modal>
  );
}

function ScheduleGameModal({ onClose, onPick }) {
  const [teams, setTeams] = useState(null);
  useEffect(() => { api.get("/live/me/teams").then(({ data }) => setTeams(data.teams)).catch(() => setTeams([])); }, []);
  return (
    <Modal title="Schedule a Game" onClose={onClose} testid="schedule-game-modal">
      <p className="text-sm text-slate-500 mb-3">Pick a team to open its schedule and add a game.</p>
      {teams === null ? <div className="text-sm text-slate-400 py-4">Loading…</div>
        : teams.length === 0 ? <div className="text-sm text-slate-500 py-4">You don't manage any teams yet.</div>
        : <div className="space-y-1">
          {teams.map((t) => (
            <button key={t.id} data-testid={`schedule-team-${t.id}`} onClick={() => onPick(t.slug)}
              className="w-full flex items-center gap-3 p-2 border border-slate-100 rounded-sm hover:border-[#E63946] text-left">
              <div className="w-8 h-8 bg-slate-100 rounded-sm grid place-items-center shrink-0 text-[10px] text-slate-400">{t.name[0]}</div>
              <div className="text-sm font-semibold truncate">{t.name}</div>
            </button>
          ))}
        </div>}
    </Modal>
  );
}
